import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { createScrollReveal } from "../../utils/createScrollReveal";

gsap.registerPlugin(SplitText, ScrollTrigger);

const EASE = "expo.out";

export default class StoryPush {
  constructor(el) {
    this.el = el;

    this.setupReveal();
  }

  setupReveal() {
    const title = this.el.querySelector(".c-story-push__title");
    if (!title) return;

    this.reveal = createScrollReveal({
      trigger: this.el,
      // The tag's appear + shake sequencing matters here regardless of scroll
      // speed, so skip the shared fast-fling bypass (other createScrollReveal
      // callers keep the default).
      velocityThreshold: Infinity,
      build: () => {
        const link = this.el.querySelector(".c-story-push__link");

        // SplitText rebuilds the title's DOM into line wrappers, so the tag
        // has to be queried after the split — grabbing it beforehand would
        // animate a node the split immediately orphans.
        const split = new SplitText(title, { type: "lines" });
        const lines = split.lines;
        const span = title.querySelector(".c-story-push__tag");

        if (link) link.style.opacity = "0";

        const tl = gsap.timeline({ delay: 0.2, paused: true });

        tl.fromTo(
          lines,
          { y: "100%", opacity: 0 },
          { y: "0%", opacity: 1, duration: 0.8, ease: EASE, stagger: 0.2 },
          0
        );

        if (span) {
          // The static tilt lives on the CSS `rotate` property (see
          // _story-push.scss), left alone here on purpose: `is-vibrating`
          // (added once this tween is done, plus a short wait) animates
          // `transform`, and a CSS animation on that property would
          // otherwise fight this tween's own transform updates every frame —
          // the tag would snap straight and never show its scale-in.
          tl.fromTo(
            span,
            { scale: 1.2, autoAlpha: 0 },
            { scale: 1, autoAlpha: 1, duration: 0.8, ease: EASE },
            0.4
          );
          tl.addLabel("tagAppeared");

          // Wait a beat after the tag has fully appeared before starting the
          // (infinite, via CSS `tag-vibrate`) shake loop.
          tl.call(() => span.classList.add("is-vibrating"), null, "tagAppeared+=1.5");
        }

        if (link) {
          tl.fromTo(link, { opacity: 0 }, { opacity: 1, duration: 1.2, ease: EASE }, "tagAppeared+=0.1");
        }

        return {
          tl,
          teardown: () => {
            split.revert();
            // revert() re-parses the title's original markup, so `span` is
            // now detached — the vibrate class (needed here too, since a
            // fast-scroll snap suppresses tl.call) has to go on the fresh
            // node revert() just put back in the DOM.
            title.querySelector(".c-story-push__tag")?.classList.add("is-vibrating");
          },
        };
      },
    });
  }

  destroy() {
    if (this.reveal) this.reveal.destroy();
  }
}
